import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './EmailBodyEditor.css';

export function isEmailBodyEmpty(html) {
  if (!html || typeof html !== 'string') return true;
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (div.textContent || '').replace(/\u00a0/g, ' ').trim();
  return !text;
}

const MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'color', 'background',
  'align',
  'link', 'image',
];

/**
 * Rich HTML body for outbound email (compose / reply). Outputs HTML for body_html.
 */
/**
 * Rich HTML body for outbound email (compose / reply). Outputs HTML for body_html.
 *
 * Supports `ref` forwarding: the ref points to the underlying ReactQuill
 * instance so callers can do `ref.current.getEditor()` to interact with the
 * Quill API (e.g. insert merge tags at the current cursor selection).
 */
const EmailBodyEditor = forwardRef(function EmailBodyEditor(
  {
    value,
    onChange,
    placeholder,
    minHeight = 280,
    className = '',
    /** Match Mailbox page borders / toolbar (uses --c-border etc. when set on parent) */
    themeMailbox = false,
    /** Shorter editor area for reply bars */
    compact = false,
    readOnly = false,
  },
  ref
) {
  const { t } = useTranslation();
  const modules = useMemo(() => MODULES, []);
  const rootClass = [
    'crm-email-body-editor',
    themeMailbox && 'crm-email-body-editor--mailbox',
    compact && 'crm-email-body-editor--compact',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClass}
      style={{ '--crm-email-editor-min-h': `${minHeight}px` }}
    >
      <ReactQuill
        ref={ref}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder || t('mailbox.messagePlaceholder')}
        readOnly={readOnly}
      />
    </div>
  );
});

export default EmailBodyEditor;
